import { useState } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { fmt } from '../../utils/format';
import { useApi } from '../../hooks/useApi';
import { numericValue } from '../../utils/numericInput';
import { Btn, Input, Select, Textarea, Modal, ModalFooter, Table, Spinner, ErrorMsg, InfoNote, EmptyState, useToast } from '../ui';
import { todayIsoInZone } from '@harvus/shared';

// ---------------------------------------------------------------------------
// ValuationTab — what we think one deal detail is worth, and what that implies.
//
// The deliberately SIMPLE alternative to the Budget tab. That tool models an
// asset — buildings, square footage, rent roll, NOI, exit cap, cost of sale —
// to arrive at a value, which is the right instrument when there is an operating
// asset to model and far too much apparatus when the answer is "about $3,200 an
// acre". So this screen holds three ways to state a value and nothing else:
//
//   $ (flat amount)     ·     $/acre × acres     ·     income ÷ cap rate
//
// EVERY MARK IS KEPT, AND THE LIST IS THE HISTORY. A valuation is not a field on
// the deal that gets overwritten; it is a dated statement, and the interesting
// thing about it is how it has moved. The as-of date is BUSINESS time — the date
// the number is an estimate FOR, not the day it was typed — so a mark can be
// backdated and corrected without rewriting anything else.
//
// TIME TO SALE BELONGS TO THE MARK, NOT TO THE ASSET. The same tract is a
// 90-day sale at $3,000/acre and a two-year sale at $4,200, so the horizon sits
// beside the price and moves with it.
//
// TWO HORIZONS, AND THEY ARE NOT THE SAME QUESTION. "It would take 90 days to
// sell" is LIQUIDITY. "We plan to sell on 31 Dec 2030" is INTENT, and it is the
// one the IRR terminates at — a $10M tract that could move in 90 days is not
// $10M of cash ninety days from now if the plan is to hold it four more years.
// The planned sale date wins wherever it is set; time to sale is the fallback
// and, when both are set, the check on whether the plan can actually be executed.
//
// THREE RATES, ONE LADDER, AND NONE MAY BE READ AS ANOTHER. They share the
// same Sage cash history and differ only in what terminal is appended and when:
//
//   Unrealized — the CURRENT mark, dated today. "If we sold now."
//   Projected  — the value at the EXIT, dated at the exit. "If we hold to plan."
//   Realized   — nothing appended. What the GL alone can prove.
//
// Each adds exactly one assumption to the one above it, which is why all three
// are on screen: one unqualified "IRR" gets quoted as whichever the reader
// assumed. On a deal still held the realized rate is brutal by construction
// (capital out, asset unsold) and is shown anyway, with the value it leaves out
// printed beside it.
//
// THE EXIT VALUE COMES FROM THE MARK SERIES, NOT FROM ONE MARK. A view that the
// tract is worth $15M at the end of 2030 is a MARK AS OF 2030 flagged as a
// projection; several of them are a forward path and the exit reads off it.
// A projection NEVER becomes the deal's carrying value — the header and the
// gain against Sage cost use the newest mark that is not a forecast.
//
// NOTHING IS COMPUTED HERE. Every dollar figure, the expected sale date and the
// IRR come from the server's `computed` block (api/src/valuation.js). A
// client-side copy of the resolver would be one more thing to keep in lockstep.
// ---------------------------------------------------------------------------

// LAND trades in two units and which one reads as "the number" is a property of
// the market, not the asset — farm ground in $/acre, commercial and development
// ground in $/SF. Both are offered so nobody converts in their head at the
// moment of entry, which is how a decimal point goes missing.
//
// $/land SF is LAND square feet, never BUILDING square feet — different
// questions, similar names, and a mark against the wrong one is out by an order
// of magnitude while looking plausible. Every label says "land" for that reason.
const METHOD_OPTIONS = [
  { value: 'Amount', label: '$ (flat amount)' },
  { value: 'PerAcre', label: '$ / Ac  (per acre × acres)' },
  { value: 'PerLandSF', label: '$ / PSF (Land)  (per land SF × land SF)' },
  { value: 'CapRate', label: 'income ÷ cap rate' },
];

const SOURCES = ['Internal Estimate', 'Appraisal', 'Broker Opinion', 'Comparable Sale', 'Offer', 'Tax Assessment', 'Other'];

const METHOD_LABEL = { Amount: 'Flat $', PerAcre: '$/Ac', PerLandSF: '$/PSF (Land)', CapRate: 'Cap rate' };

const today = () => todayIsoInZone();

// Date arithmetic for the modal's live preview ONLY — the exit date the app
// actually stores and solves against is always the server's (see "NOTHING IS
// COMPUTED HERE"). This exists so the form can say which date it is about to
// use before you save it, which is the whole point of having two horizons.
const addDaysISO = (iso, n) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso) || !Number.isFinite(Number(n))) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + Math.round(Number(n)));
  return isNaN(d) ? null : d.toISOString().slice(0, 10);
};
const daysBetweenISO = (a, b) => {
  if (!a || !b) return null;
  const ms = new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`);
  return isNaN(ms) ? null : Math.round(ms / 86400000);
};

const EMPTY = {
  asOfDate: today(), method: 'Amount', amount: '', pricePerAcre: '', pricePerLandSF: '', acres: '',
  annualIncome: '', capRate: '', timeToSaleDays: '', plannedSaleDate: '',
  isProjection: false, source: 'Internal Estimate', notes: '',
};

// A hold in words. "1,645 d" is technically the answer and nobody reads it as
// four and a half years, which is the fact that changes how the rate beside it
// should be read.
const holdLabel = (d) => {
  if (d == null) return null;
  if (d < 45) return `${d} day${d === 1 ? '' : 's'}`;
  if (d < 365) return `${Math.round(d / 30.4)} months`;
  const years = d / 365.25;
  return `${years < 10 ? years.toFixed(1) : Math.round(years)} years`;
};

// A rate arrives as a fraction (0.1309) and a cap rate as a whole-number percent
// (7.5). Two different conventions on one screen, so neither is guessed at.
const rate = (v) => (v == null ? '—' : fmt.pct(v));
const money = (v) => (v == null ? '—' : fmt.currency(v));
const days = (v) => (v == null ? '—' : `${Number(v).toLocaleString('en-US')} d`);
// fmt.currency rounds to whole dollars, which turns $2.75/SF into "$3". A
// per-SF figure is small and its cents are the whole number.
const perSf = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
const signedMoney = (v) => (v == null ? '—' : `${v < 0 ? '−' : '+'}${fmt.currency(Math.abs(v))}`);

const moveColor = (v) => (v == null ? B.textMd : v > 0 ? B.green : v < 0 ? B.red : B.textMd);

// How the exit value was arrived at, in words. A figure read straight off a
// mark and one interpolated between two are different claims, and a reader
// deciding whether to quote the rate needs to know which.
const VALUE_BASIS = {
  exact: (d) => `a mark dated ${fmt.date(d[0])}`,
  interpolated: (d) => `interpolated between ${fmt.date(d[0])} and ${fmt.date(d[1])}`,
  heldFlat: (d) => `the ${fmt.date(d[0])} mark, held flat (no later mark)`,
  firstMark: (d) => `the ${fmt.date(d[0])} mark (the earliest recorded)`,
};
const valueBasisText = (basis, dates) =>
  (basis && VALUE_BASIS[basis] && Array.isArray(dates) && dates.length
    ? VALUE_BASIS[basis](dates) : null);

// A NEW MARK INHERITS THE PLAN, BECAUSE THE PLAN HAS NOT CHANGED. Re-marking a
// property is routine — a fresh appraisal, a new comp — and the exit date
// usually is not part of what moved. Starting each new mark blank would silently
// revert the exit to the liquidity horizon, and the IRR would leap from 6% to
// 15% because somebody updated a price. So the form is PRE-FILLED from the
// current mark, in a box the user can plainly see and empty.
//
// Pre-filling the FORM, never the write: the API stores exactly what it is sent,
// so deleting the date really does clear the plan.
const newForm = (current, parcelAcres) => ({
  ...EMPTY,
  // Deliberately NOT inherited from the current mark: a valuation of today is
  // the ordinary case and a forecast is the deliberate one, so the form opens
  // on the ordinary one every time and the date drives the default at the API.
  isProjection: false,
  acres: parcelAcres ?? '',
  plannedSaleDate: (current && current.plannedSaleDate) || '',
  timeToSaleDays: (current && current.timeToSaleDays != null) ? current.timeToSaleDays : '',
});

// ---------------------------------------------------------------------------

export const ValuationTab = ({ dealId, dealDetailId }) => {
  const { toast } = useToast();
  const base = `deals/${dealId}/details/${dealDetailId}/valuations`;
  const { data, loading, error, reload } = useApi(() => api.get(base), [dealDetailId]);

  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);   // { editing?, form } | null

  if (loading) return <Spinner label="Loading valuations…" />;
  if (error) return <ErrorMsg msg={error.message || 'Failed to load valuations'} onRetry={reload} />;

  // Pre-migration-171 database: say so rather than showing an empty list that
  // silently refuses every write.
  if (data && data.available === false) {
    return <div style={{ marginTop: 8 }}><InfoNote>{data.reason}</InfoNote></div>;
  }

  const rows = (data && data.valuations) || [];
  const computed = (data && data.computed) || {};
  const current = computed.current || null;
  const irr = computed.irr || {};
  const actual = computed.actualIrr || {};
  const unreal = computed.unrealizedIrr || {};
  const valueBasisLine = valueBasisText(irr.valueBasis, irr.valueFromDates);
  const hasPath = Array.isArray(computed.path) && computed.path.length > 0;
  // Debt is only worth a column when there is some — an unlevered deal should
  // not grow a "Debt $0" cell and a longer sentence explaining a subtraction
  // that did not happen. Half a cent, so a DECIMAL rounding tail cannot show up
  // as debt.
  const hasDebt = irr.debtOutstanding != null && Math.abs(Number(irr.debtOutstanding)) >= 0.005;
  const parcelAcres = data && data.parcelAcres;

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); reload(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const save = (form, editing) => run(async () => {
    const payload = {
      asOfDate: form.asOfDate,
      method: form.method,
      amount: form.method === 'Amount' ? form.amount : null,
      pricePerAcre: form.method === 'PerAcre' ? form.pricePerAcre : null,
      pricePerLandSF: form.method === 'PerLandSF' ? form.pricePerLandSF : null,
      // Both land methods resolve against the same acreage — the unit changes,
      // the land does not.
      acres: (form.method === 'PerAcre' || form.method === 'PerLandSF')
        ? (form.acres === '' ? null : form.acres) : null,
      annualIncome: form.method === 'CapRate' ? form.annualIncome : null,
      capRate: form.method === 'CapRate' ? form.capRate : null,
      timeToSaleDays: form.timeToSaleDays === '' ? null : form.timeToSaleDays,
      // An empty box is a deliberate null — clearing the plan is how a mark goes
      // back to being dated off the liquidity horizon.
      plannedSaleDate: form.plannedSaleDate === '' ? null : form.plannedSaleDate,
      isProjection: !!form.isProjection,
      source: form.source || null,
      notes: form.notes || null,
    };
    if (editing) await api.put(`${base}/${editing.valuationID}`, payload);
    else await api.post(base, payload);
    setModal(null);
  });

  const remove = (row) => run(async () => {
    if (!window.confirm(`Delete the valuation as of ${fmt.date(row.asOfDate)}?`)) return;
    await api.del(`${base}/${row.valuationID}`);
  });

  const toForm = (r) => ({
    asOfDate: r.asOfDate || today(),
    method: r.method || 'Amount',
    amount: r.amount ?? '',
    pricePerAcre: r.pricePerAcre ?? '',
    pricePerLandSF: r.pricePerLandSF ?? '',
    acres: r.acres ?? '',
    annualIncome: r.annualIncome ?? '',
    capRate: r.capRate ?? '',
    timeToSaleDays: r.timeToSaleDays ?? '',
    plannedSaleDate: r.plannedSaleDate || '',
    isProjection: !!r.isProjection,
    source: r.source || '',
    notes: r.notes || '',
  });

  return (
    <div style={{ marginTop: 8 }}>
      {/* ── Headline: the mark we are using, and what it implies ──────────── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', border: `1px solid ${B.border}`, borderRadius: 12, background: B.surface, marginBottom: 18, overflow: 'hidden' }}>
        <Cell k="Current valuation" v={money(current && current.value)}
              sub={current ? `as of ${fmt.date(current.asOfDate)} · ${METHOD_LABEL[current.method] || current.method}` : 'no valuation yet'} />
        {/* THE EXIT, NOT THE LIQUIDITY HORIZON. Which of the two dated it is on
            the face of the cell, because the same rate below means quite
            different things depending on the answer. */}
        <Cell k="Exit date"
              v={current && current.exitDate ? fmt.date(current.exitDate) : '—'}
              sub={exitSub(current)} />
        {/* BOTH implied units, whatever method produced the mark — that is the
            point of holding two: a $/SF mark and a $/acre mark become directly
            comparable without anyone converting in their head. */}
        <Cell k="Implied $/Ac" v={current && current.impliedPerAcre != null ? fmt.currency(current.impliedPerAcre) : '—'}
              sub={current && current.acres ? `${Number(current.acres).toLocaleString('en-US')} acres` : (parcelAcres ? `${Number(parcelAcres).toLocaleString('en-US')} parcel acres` : 'no acreage')} />
        <Cell k="Implied $/PSF" v={perSf(current && current.impliedPerLandSF)}
              sub={current && current.acres ? `${Math.round(Number(current.acres) * 43560).toLocaleString('en-US')} land SF` : 'land, not building'} />
        {/* WHAT IT IS ASSUMED TO SELL FOR, beside what it is carried at. With no
            forecast marks this is the current mark held flat and the two cells
            agree — which is the pre-174 behaviour, visible rather than implied. */}
        <Cell k="Value at exit" v={money(irr.grossValue)}
              sub={valueBasisLine || (irr.saleDate ? 'no valuation to sell at' : 'no exit date set')} />
        <Cell k="Total cost (Sage)" v={money(computed.costBasis)}
              sub={computed.gainLoss != null ? `${signedMoney(computed.gainLoss)} vs cost` : 'no Sage location tagged'}
              color={moveColor(computed.gainLoss)} />
        {/* Both sides of the subtraction the IRR makes, shown rather than
            asserted — a rate that quietly nets off six figures of debt is one
            nobody can check. */}
        {hasDebt && (
          <Cell k="Debt outstanding" v={money(irr.debtOutstanding)}
                sub={irr.terminalValue != null ? `${money(irr.terminalValue)} net at sale` : 'repaid at sale'} />
        )}
      </div>

      {/* ── THREE RATES, ONE LADDER ─────────────────────────────────────────
          Same Sage cash history in all three; they differ only in what terminal
          is appended and when. Each adds exactly one assumption to the one
          before it, and each says what it counts — because one unqualified
          "IRR" gets quoted as whichever the reader assumed. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <RateCard
          k="Unrealized IRR"
          v={rate(unreal.irr)}
          color={unreal.irr != null ? moveColor(unreal.irr) : B.textMd}
          sub={unreal.irr != null
            ? `If we sold today at the ${money(unreal.grossValue)} mark — ${money(unreal.terminalValue)} net${
                unreal.markAgeDays != null && unreal.markAgeDays > 0
                  ? `, valued ${holdLabel(unreal.markAgeDays)} ago` : ''}`
            : (unreal.reason || 'not solvable yet')} />
        <RateCard
          k="Projected IRR"
          v={rate(irr.irr)}
          color={irr.irr != null ? moveColor(irr.irr) : B.textMd}
          sub={irr.irr != null
            ? `Held to ${fmt.date(irr.saleDate)} and sold at ${money(irr.terminalValue)} net${
                irr.holdDays != null ? ` — ${holdLabel(irr.holdDays)} out` : ''}`
            : (irr.reason || 'not solvable yet')} />
        <RateCard
          k="Realized IRR"
          v={rate(actual.irr)}
          color={actual.irr != null ? moveColor(actual.irr) : B.textMd}
          /* THE CAVEAT IS THE POINT OF THE CARD. On a deal still held this rate
             is deeply negative by construction — capital out, asset unsold —
             and it is only honest beside the value it leaves out. */
          sub={actual.irr != null
            ? (actual.excludesUnrealized
                ? `Sage cash only — excludes the ${money(actual.unrealizedValue)} the property is carried at, unsold`
                : 'Sage cash only — nothing unrealized left to count')
            : (actual.reason || 'not solvable yet')}
          warn={actual.irr != null && actual.excludesUnrealized} />
      </div>

      {/* THE IRR EXPLAINS ITSELF OR EXPLAINS WHY IT CANNOT. A blank rate invites
          the reader to assume the deal is at zero rather than unanswerable. */}
      {irr.reason && <div style={{ marginBottom: 16 }}><InfoNote>{irr.reason}</InfoNote></div>}
      {irr.irr != null && (
        <div style={{ marginBottom: 16, fontSize: 12, color: B.textMd, lineHeight: 1.6 }}>
          Solved from <strong>{irr.flowCount}</strong> Sage GL postings against this deal detail
          {irr.firstFlowDate ? <> since {fmt.date(irr.firstFlowDate)}</> : null} — {money(irr.invested)} deployed,
          {' '}{money(irr.returned)} returned — plus an assumed sale on <strong>{fmt.date(irr.saleDate)}</strong>
          {irr.saleDateBasis === 'planned'
            ? <> (the planned exit{irr.holdDays != null ? <>, {holdLabel(irr.holdDays)} out</> : null})</>
            : <> (no planned exit set, so the {days(irr.timeToSaleDays)} time to sale dated it)</>}
          {' '}at {valueBasisLine ? <>the value the marks imply then (<ins>{valueBasisLine}</ins>)</> : 'the current mark'}
          {irr.assumedGain != null && Math.abs(irr.assumedGain) >= 0.005 && (
            <> — <strong>{money(irr.grossValue)}</strong>, {signedMoney(irr.assumedGain)} against
            the {money(irr.markValue)} it is carried at today</>
          )}
          {' '}—
          {hasDebt
            ? <> {money(irr.grossValue)} less {money(irr.debtOutstanding)} of debt = <strong>{money(irr.terminalValue)}</strong> net.</>
            : <> {money(irr.terminalValue)}.</>}
          {' '}Total value multiple <strong>{irr.totalValueMultiple != null ? `${irr.totalValueMultiple.toFixed(2)}×` : '—'}</strong>
          {' '}(includes the unrealized mark; realized alone is {irr.realizedMultiple != null ? `${irr.realizedMultiple.toFixed(2)}×` : '—'}).
          {/* THE EXIT IS NOT A CASH FLOW AND THE MARK IS NOT ESCALATED. Both are
              easy to forget once the rate is on screen, and both change how it
              should be quoted — so neither is left to be inferred. */}
          <br />
          The sale is not a recorded cash flow — it is what the marks say the property is worth on that
          date, standing in for a sale nobody has made.
          {hasPath
            ? <> The exit value comes from {computed.path.length} forecast mark{computed.path.length === 1 ? '' : 's'},
              each dated and sourced; none of them is treated as cash, and the deal is still carried at
              the {money(irr.markValue)} mark of {fmt.date(irr.markAsOfDate)}.</>
            : <> There are no forecast marks, so it is held <strong>flat</strong> to {fmt.date(irr.saleDate)}
              with no appreciation assumed. To say it will be worth more then, add a valuation as of that
              date and tick <em>Projection</em>.</>}
          {irr.saleDateBasis !== 'planned' && (
            <> <span style={{ color: B.amber }}>
              This assumes we sell as soon as we could, not when we plan to. Set a planned sale date on the
              valuation if the intention is to hold.
            </span></>
          )}
          {irr.plannedBeforeLiquidity && (
            <> <span style={{ color: B.amber }}>
              The planned exit is sooner than the {days(irr.timeToSaleDays)} it would take to sell at this
              price — the earliest a sale could close is {fmt.date(irr.earliestSaleDate)}.
            </span></>
          )}
          {irr.saleDateInPast && <> <span style={{ color: B.red }}>The exit date has passed — re-mark it.</span></>}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '4px 0 12px' }}>
        <span style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.6, color: B.textMd, fontWeight: 700 }}>
          Valuation history
        </span>
        <Btn onClick={() => setModal({ form: newForm(current, parcelAcres) })} disabled={busy}>+ Add valuation</Btn>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon="info" title="No valuations yet"
          message="Record what you think this is worth — a flat amount, a price per acre, or income over a cap rate — plus when you plan to sell, which is what the IRR uses as the exit."
          action={<Btn onClick={() => setModal({ form: newForm(current, parcelAcres) })}>+ Add valuation</Btn>} />
      ) : (
        <Table
          columns={[
            { key: 'asOfDate', label: 'As of',
              render: (v, r) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                  {fmt.date(v)}
                  {/* A forecast and a valuation of that date are different
                      claims and must never read alike in a list. */}
                  {r.isProjection && (
                    <span title="A forecast for that date — it supplies the exit value but is never the deal's carrying value"
                          style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4,
                                   textTransform: 'uppercase', color: B.amber,
                                   border: `1px solid ${B.amber}`, borderRadius: 4, padding: '1px 4px' }}>
                      forecast
                    </span>
                  )}
                </span>
              ) },
            { key: 'value', label: 'Value', align: 'right',
              render: (v, r) => (v == null
                // An unresolvable mark is shown as unresolvable, never as $0 — a
                // property worth nothing and one nobody finished describing are
                // different claims.
                ? <span style={{ color: B.amber }} title={r.unresolvedReason}>incomplete</span>
                : <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt.currency(v)}</strong>) },
            { key: 'explain', label: 'How', render: (v) => <span style={{ fontSize: 12, color: B.textMd }}>{v || '—'}</span> },
            { key: 'timeToSaleDays', label: 'Time to sale', align: 'right',
              render: (v, r) => (v == null ? '—' : (
                <span style={{ fontVariantNumeric: 'tabular-nums' }} title={r.expectedSaleDate ? `Could be sold by ${fmt.date(r.expectedSaleDate)}` : ''}>
                  {days(v)}
                </span>
              )) },
            // THE DATE THE IRR ON THIS ROW TERMINATES AT, with its basis shown
            // rather than left to be inferred: a planned exit and a fallback
            // read identically as a date and mean quite different things.
            { key: 'exitDate', label: 'Exit', render: (v, r) => (v ? (
              <span title={r.exitBasis === 'planned'
                ? `Planned exit${r.expectedSaleDate ? ` — could be sold from ${fmt.date(r.expectedSaleDate)}` : ''}`
                : 'No planned exit on this mark — dated from the time to sale'}>
                {fmt.date(v)}
                {r.exitBasis !== 'planned' && <span style={{ color: B.textLt, fontSize: 11 }}> (earliest)</span>}
                {r.plannedBeforeLiquidity && <span style={{ color: B.amber }} title="Planned before it could be sold at this price"> ⚠</span>}
              </span>
            ) : '—') },
            { key: 'irr', label: 'IRR', align: 'right',
              render: (v, r) => (v == null && r.isProjection
                ? <span style={{ color: B.textLt }} title="A forecast has no exit of its own — it supplies the value the projected IRR sells at">—</span>
                : <span style={{ fontVariantNumeric: 'tabular-nums', color: moveColor(v) }}>{rate(v)}</span>) },
            { key: 'changeAmount', label: 'Change', align: 'right',
              render: (v, r) => (v == null ? '—' : (
                <span style={{ color: moveColor(v), fontVariantNumeric: 'tabular-nums' }}>
                  {signedMoney(v)}{r.changePct != null ? ` (${(r.changePct * 100).toFixed(1)}%)` : ''}
                </span>
              )) },
            { key: 'source', label: 'Source', render: (v) => v || '—' },
            { key: 'actions', label: '', align: 'right',
              render: (_v, r) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                  <Btn size="sm" variant="ghost" onClick={() => setModal({ editing: r, form: toForm(r) })} disabled={busy}>Edit</Btn>
                  <Btn size="sm" variant="ghost" onClick={() => remove(r)} disabled={busy}>Delete</Btn>
                </span>
              ) },
          ]}
          data={rows.map((r) => ({ ...r, id: r.valuationID }))}
        />
      )}

      {modal && (
        <ValuationModal
          form={modal.form}
          editing={modal.editing}
          parcelAcres={parcelAcres}
          allowedMethods={(data && data.methods) || null}
          supportsPlannedSaleDate={data ? data.supportsPlannedSaleDate !== false : true}
          supportsProjection={data ? data.supportsProjection !== false : true}
          busy={busy}
          onCancel={() => setModal(null)}
          onSave={(f) => save(f, modal.editing)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------

// The one line under the exit date, saying where the date came from. A planned
// exit and a fallback are not interchangeable and the reader has to be able to
// tell without opening the row.
const exitSub = (current) => {
  if (!current || !current.exitDate) return 'no exit date — no IRR';
  if (current.exitBasis === 'planned') {
    return current.timeToSaleDays != null
      ? `planned · ${days(current.timeToSaleDays)} to sell`
      : 'planned sale date';
  }
  return `from ${days(current.timeToSaleDays)} to sell · no plan set`;
};

// One of the two rates. Bigger than a Cell because the caveat under it is
// load-bearing: the realized rate is meaningless without the value it omits,
// and a sub-line small enough to skip would be worse than not showing it.
const RateCard = ({ k, v, sub, color, warn }) => (
  <div style={{
    flex: '1 1 240px', minWidth: 230, padding: '14px 18px',
    border: `1px solid ${warn ? B.amber : B.border}`, borderRadius: 12,
    background: warn ? '#fdf9f2' : B.surface,
  }}>
    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: B.textLt, fontWeight: 700 }}>{k}</div>
    <div style={{ fontSize: 24, fontWeight: 700, marginTop: 2, fontVariantNumeric: 'tabular-nums', color: color || B.text }}>{v}</div>
    <div style={{ fontSize: 11.5, color: warn ? B.amber : B.textMd, marginTop: 4, lineHeight: 1.5 }}>{sub}</div>
  </div>
);

const Cell = ({ k, v, sub, color }) => (
  <div style={{ padding: '12px 18px', borderRight: `1px solid ${B.divider}`, minWidth: 150, flex: '1 1 150px' }}>
    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: B.textLt, fontWeight: 600 }}>{k}</div>
    <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2, fontVariantNumeric: 'tabular-nums', color: color || B.text }}>{v}</div>
    {sub && <div style={{ fontSize: 11, color: B.textLt, marginTop: 2 }}>{sub}</div>}
  </div>
);

// ONE FORM, THREE METHODS, AND ONLY THE CHOSEN METHOD'S BOXES ARE SHOWN. Three
// sets of inputs on screen at once invites filling in two of them and leaves the
// reader guessing which one produced the number.
const ValuationModal = ({ form: initial, editing, parcelAcres, allowedMethods, supportsPlannedSaleDate, supportsProjection, busy, onCancel, onSave }) => {
  const [form, setForm] = useState(initial);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  // The server says which methods this database can actually store (PerLandSF
  // needs migration 172), so an unsupported one is never offered rather than
  // being offered and refused on save.
  const methods = allowedMethods
    ? METHOD_OPTIONS.filter((m) => allowedMethods.includes(m.value))
    : METHOD_OPTIONS;

  return (
    <Modal title={editing ? 'Edit valuation' : 'Add valuation'} onClose={onCancel} width={620}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Input label="As of date" type="date" value={form.asOfDate} onChange={set('asOfDate')} required />
        {/* WHAT KIND OF CLAIM THIS IS, in words rather than a bare tickbox —
            the two behave differently enough that the reader has to be told
            which they are entering, not left to infer it from a checkbox. */}
        {supportsProjection ? (
          <Select label="Kind" value={form.isProjection ? 'projection' : 'valuation'}
                  onChange={(v) => set('isProjection')(v === 'projection')}
                  options={[
                    { value: 'valuation', label: 'Valuation — what it is worth on that date' },
                    { value: 'projection', label: 'Projection — a forecast for that date' },
                  ]} />
        ) : <div />}
        <Select label="Method" value={form.method} onChange={set('method')} options={methods} required />

        {form.method === 'Amount' && (
          <Input label="Value ($)" type="number" value={form.amount} onChange={set('amount')} required placeholder="e.g. 420000" />
        )}

        {form.method === 'PerAcre' && (
          <>
            <Input label="Price per acre ($)" type="number" value={form.pricePerAcre} onChange={set('pricePerAcre')} required placeholder="e.g. 3200" />
            <Input label="Acres" type="number" value={form.acres} onChange={set('acres')}
                   placeholder={parcelAcres ? `blank = ${Number(parcelAcres).toLocaleString('en-US')} from parcels` : 'acres'} />
          </>
        )}

        {form.method === 'PerLandSF' && (
          <>
            <Input label="Price per land SF ($)" type="number" value={form.pricePerLandSF} onChange={set('pricePerLandSF')} required placeholder="e.g. 2.75" />
            <Input label="Acres" type="number" value={form.acres} onChange={set('acres')}
                   placeholder={parcelAcres ? `blank = ${Number(parcelAcres).toLocaleString('en-US')} from parcels` : 'acres'} />
          </>
        )}

        {form.method === 'CapRate' && (
          <>
            <Input label="Annual income ($)" type="number" value={form.annualIncome} onChange={set('annualIncome')} required placeholder="e.g. 75000" />
            <Input label="Cap rate (%)" type="number" value={form.capRate} onChange={set('capRate')} required placeholder="e.g. 7.5" />
          </>
        )}

        {/* TWO HORIZONS, SIDE BY SIDE, BECAUSE THEY ARE DIFFERENT QUESTIONS.
            Put one on screen alone and it gets used for both. */}
        <Input label="Time to sale (days)" type="number" value={form.timeToSaleDays} onChange={set('timeToSaleDays')}
               placeholder="e.g. 90" />
        {supportsPlannedSaleDate && (
          <Input label="Planned sale date" type="date" value={form.plannedSaleDate} onChange={set('plannedSaleDate')} />
        )}
        <Select label="Source" value={form.source} onChange={set('source')}
                options={[{ value: '', label: '—' }, ...SOURCES.map((s) => ({ value: s, label: s }))]} />
      </div>

      {/* Which date the IRR will actually use, shown while the form is open —
          the whole point of separating the two is lost if you have to save and
          look at the table to find out which one won. */}
      {(() => {
        const planned = form.plannedSaleDate || null;
        const daysN = form.timeToSaleDays === '' ? null : Number(form.timeToSaleDays);
        const earliest = (form.asOfDate && daysN != null && !isNaN(daysN)) ? addDaysISO(form.asOfDate, daysN) : null;
        const exit = planned || earliest;
        if (!exit) {
          return (
            <div style={{ marginTop: 10, padding: '8px 12px', background: B.surfaceMuted, borderRadius: 8, fontSize: 12, color: B.amber }}>
              With neither a planned sale date nor a time to sale there is no dated exit, so this mark will show no IRR.
            </div>
          );
        }
        const held = holdLabel(daysBetweenISO(form.asOfDate, exit));
        return (
          <div style={{ marginTop: 10, padding: '8px 12px', background: B.surfaceMuted, borderRadius: 8, fontSize: 12, color: B.textMd }}>
            IRR will assume a sale on <strong>{fmt.date(exit)}</strong>
            {held ? <> — {held} after the as-of date</> : null}
            {planned ? ' (the planned exit).' : ' (the earliest we could be out — no planned exit set).'}
            {planned && earliest && planned < earliest && (
              <span style={{ color: B.amber }}> The plan is sooner than the {daysN} days it would take to sell at this price.</span>
            )}
          </div>
        );
      })()}

      <div style={{ marginTop: 14 }}>
        <Textarea label="Notes" value={form.notes} onChange={set('notes')} rows={2}
                  placeholder="What this is based on — a comp, a broker call, an appraisal…" />
      </div>

      {/* The conversion, shown live. A decimal point in the wrong place is
          invisible in one unit and obvious in the other. */}
      {(form.method === 'PerAcre' || form.method === 'PerLandSF') && (() => {
        const acres = Number(form.acres !== '' ? form.acres : parcelAcres);
        const per = Number(form.method === 'PerAcre' ? form.pricePerAcre : form.pricePerLandSF);
        if (!(acres > 0) || !(per > 0)) return null;
        const value = form.method === 'PerAcre' ? per * acres : per * acres * 43560;
        const perAc = value / acres;
        const perSfVal = value / (acres * 43560);
        return (
          <div style={{ marginTop: 4, marginBottom: 8, padding: '8px 12px', background: B.surfaceMuted, borderRadius: 8, fontSize: 12, color: B.textMd, fontVariantNumeric: 'tabular-nums' }}>
            {fmt.currency(value)} · {fmt.currency(perAc)}/Ac · ${perSfVal.toFixed(2)}/PSF
            {' '}over {acres.toLocaleString('en-US')} ac ({Math.round(acres * 43560).toLocaleString('en-US')} land SF)
          </div>
        );
      })()}

      {/* THE SAME LIVE CHECK THE LAND METHODS GET, AND FOR A SHARPER REASON.
          A cap rate here is a WHOLE-NUMBER PERCENT — 8 for 8%, the convention
          every rate input on this platform uses — so ".08" is not 8%, it is
          eight hundredths of one percent, and it resolves the property at a
          hundred times what was meant. Nothing about the figure looks wrong on
          the page; the only thing that makes it obvious is the dollar value it
          produces, so that is shown before it can be saved. */}
      {form.method === 'CapRate' && (() => {
        const income = numericValue(form.annualIncome);
        const capRate = numericValue(form.capRate);
        if (income == null || !(capRate > 0)) return null;
        const value = income / (capRate / 100);
        const acres = Number(parcelAcres);
        // Under one percent is not a cap rate anybody underwrites; it is a
        // fraction typed into a field that wants a percent. Flagged rather than
        // refused — the engine cannot know intent, and a legitimate-but-tiny
        // rate is still the user's to enter.
        const looksLikeFraction = capRate < 1;
        return (
          <div style={{
            marginTop: 4, marginBottom: 8, padding: '8px 12px', borderRadius: 8, fontSize: 12,
            fontVariantNumeric: 'tabular-nums',
            background: looksLikeFraction ? '#fdf9f2' : B.surfaceMuted,
            color: looksLikeFraction ? B.amber : B.textMd,
          }}>
            {fmt.currency(income)} ÷ {capRate}% = <strong>{fmt.currency(value)}</strong>
            {acres > 0 && <> · {fmt.currency(value / acres)}/Ac over {acres.toLocaleString('en-US')} parcel acres</>}
            {looksLikeFraction && (
              <div style={{ marginTop: 4 }}>
                A cap rate is a whole-number percent here — enter <strong>{(capRate * 100).toLocaleString('en-US')}</strong> for
                {' '}{(capRate * 100).toLocaleString('en-US')}%, not {capRate}.
              </div>
            )}
          </div>
        );
      })()}

      <div style={{ marginTop: 12, fontSize: 11, color: B.textLt, lineHeight: 1.6 }}>
        {/* The acreage rule is genuinely surprising, so it is stated where the
            decision is made rather than left to be discovered later. */}
        {(form.method === 'PerAcre' || form.method === 'PerLandSF') && (
          <>Leave <strong>Acres</strong> blank to track the deal detail&apos;s parcels — the value then moves as parcels are added or sold.
            Enter a number to pin this mark to the acreage it was made against.
            {form.method === 'PerLandSF' && <> This is <strong>land</strong> square footage (43,560 SF to the acre), not building SF.</>}
            <br /></>
        )}
        <strong>Time to sale</strong> is how long it would take to sell <em>at this price</em> — liquidity, not intent.
        <br />
        <strong>Planned sale date</strong> is when you actually plan to be out, and it is what the IRR terminates at.
        A property worth $10M that could move in 90 days is not $10M of cash in 90 days if the plan is to hold it to 2030 —
        leave this blank only when there is genuinely no plan, and the earliest sale date (as-of + time to sale) is used instead.
        With neither, there is no IRR.
        <br />
        <br />
        <strong>Kind</strong> decides whether this figure is what the property is worth or what you expect it to be
        worth. A <strong>projection</strong> supplies the value the IRR sells at, and can sit years out — but it never
        becomes the deal&apos;s carrying value or its gain against Sage cost, which always come from the newest
        ordinary valuation. Several projections form a path (2026 $10M, 2027 $12M, 2030 $15M …); the exit reads the
        value off it at the sale date, interpolating between two marks and holding flat past the last.
        <br />
        With no projections the current mark is held <strong>flat</strong> to the exit — no appreciation assumed.
      </div>

      <ModalFooter>
        <Btn variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Btn>
        <Btn onClick={() => onSave(form)} disabled={busy} loading={busy}>{editing ? 'Save' : 'Add valuation'}</Btn>
      </ModalFooter>
    </Modal>
  );
};
